import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { mdiCheckDecagram } from "@mdi/js";
import Icon from "@mdi/react";
import { Alert, Button, Checkbox, Input, Select, Spin } from "antd";

import { api } from "../../api";
import { getCountryName } from "../../api/countries";
import AuthCard from "../../components/auth/AuthCard";
import { userStore } from "../../stores/userStore";
import { sendCounterEvent } from "../../utils/counterService";
import { usePageTitle } from "../../utils/usePageTitle";
import { usePendingAction } from "../../utils/usePendingAction";
import { isValidPhone } from "../../utils/validatePhone";

import "./SignupDetailsPage1.css";

import type { Country } from "../../api/countries";

export const CompleteRegistrationPage = () => {
  const { t, i18n } = useTranslation();
  usePageTitle(t("signup.details.formHeader"));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState<string | undefined>(undefined);
  const [countries, setCountries] = useState<Country[]>([]);
  const [phone, setPhone] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [receiveNews, setReceiveNews] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitError, setSubmitError] = useState(false);
  const { pending: submitting, run: runCreate } = usePendingAction();

  const googleEmail = searchParams.get("email") ?? "";

  useEffect(() => {
    const init = async () => {
      const [countriesList, userInfo] = await Promise.all([
        api.countries.getCountries(i18n.language),
        api.auth.getRegisterUserInfo(),
      ]);
      setCountries(countriesList);
      if (userInfo.first_name) setFirstName(userInfo.first_name);
      if (userInfo.last_name) setLastName(userInfo.last_name);
      if (userInfo.phone) setPhone(userInfo.phone);

      try {
        const current = await api.countries.getCurrentCountry(i18n.language);
        if (current.code) setCountry(current.code);
      } catch {
        // IP detection is best-effort
      }

      setLoading(false);
    };

    init().catch(() => {
      navigate("/login/1", { replace: true });
    });
  }, [navigate, i18n.language]);

  const countryOptions = countries.map((c) => ({
    value: c.code,
    label: getCountryName(c, i18n.language),
  }));

  const isRussia = country === "RU";
  const phoneValid = isValidPhone(phone);

  async function handleCreate() {
    setSubmitted(true);
    if (!firstName || !lastName || !country) return;
    if (isRussia && (!phone || !phoneValid)) return;
    if (!agreedToTerms) return;

    await runCreate(async () => {
      setSubmitError(false);
      sendCounterEvent("google_auth");
      try {
        const result = await api.auth.completeGoogleRegistration({
          first_name: firstName || undefined,
          last_name: lastName || undefined,
          country: country ? country.toLowerCase() : undefined,
          phone: isRussia && phone ? phone.replace(/\s/g, "") : undefined,
          agreed_to_newsletter: receiveNews,
          agreed_to_terms: agreedToTerms,
          lang: navigator.language.split("-")[0],
        });
        if (result.ok) {
          userStore.clear();
          sessionStorage.setItem("justRegistered", "true");
          if (result.redirect_uri) {
            window.location.href = result.redirect_uri;
          } else {
            navigate("/profile", { replace: true });
          }
        } else {
          setSubmitError(true);
        }
      } catch {
        setSubmitError(true);
      }
    });
  }

  if (loading) {
    return (
      <AuthCard>
        <Spin />
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      {googleEmail && (
        <>
          <span className="signup-details__email-chip">
            <Icon color="#4caf50" path={mdiCheckDecagram} size={0.7} />
            {googleEmail}
          </span>
          <p className="signup-details__verified">
            {t("signup.details.verifiedText")}
          </p>
        </>
      )}

      <h1 className="signup-details__form-header">
        {t("signup.details.formHeader")}
      </h1>

      <Input
        placeholder={t("signup.details.firstName")}
        size="large"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        autoComplete="given-name"
        status={submitted && !firstName ? "error" : undefined}
      />
      {submitted && !firstName && (
        <div className="signup-details__field-error">
          {t("signup.details.required")}
        </div>
      )}

      <Input
        placeholder={t("signup.details.lastName")}
        size="large"
        style={{ marginTop: 12 }}
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        autoComplete="family-name"
        status={submitted && !lastName ? "error" : undefined}
      />
      {submitted && !lastName && (
        <div className="signup-details__field-error">
          {t("signup.details.required")}
        </div>
      )}

      <Select
        placeholder={t("signup.details.country")}
        size="large"
        style={{ width: "100%", marginTop: 12 }}
        value={country}
        onChange={setCountry}
        className="select-search"
        classNames={{ input: "select-search__input" }}
        showSearch={{ optionFilterProp: "label" }}
        options={countryOptions}
        status={submitted && !country ? "error" : undefined}
      />
      {submitted && !country && (
        <div className="signup-details__field-error">
          {t("signup.details.required")}
        </div>
      )}

      {isRussia && (
        <>
          <Input
            placeholder={t("signup.details.phone")}
            size="large"
            style={{ marginTop: 12 }}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
            status={submitted && (!phone || !phoneValid) ? "error" : undefined}
          />
          {submitted && !phone && (
            <div className="signup-details__field-error">
              {t("signup.details.required")}
            </div>
          )}
          {submitted && phone && !phoneValid && (
            <div className="signup-details__field-error">
              {t("signup.details.phoneInvalid")}
            </div>
          )}
        </>
      )}

      <div className="signup-details__checkboxes">
        <div>
          <Checkbox
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
          >
            <Trans
              i18nKey="signup.details.agreeToTerms"
              components={{
                termsLink: (
                  <a
                    href={t("signup.details.termsUrl")}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
                privacyLink: (
                  <a
                    href={t("signup.details.privacyUrl")}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </Checkbox>
          {submitted && !agreedToTerms && (
            <div className="signup-details__field-error signup-details__field-error--checkbox">
              {t("signup.details.agreeRequired")}
            </div>
          )}
        </div>

        <Checkbox
          checked={receiveNews}
          onChange={(e) => setReceiveNews(e.target.checked)}
        >
          <Trans
            i18nKey="signup.details.receiveNews"
            components={{
              nextgisLink: (
                <a
                  href={t("signup.details.nextgisNewsUrl")}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </Checkbox>
      </div>

      {submitError && (
        <Alert
          type="error"
          style={{ marginTop: 20 }}
          className="signup-details__alert"
          classNames={{ description: "signup-details__alert-description" }}
          description={t("signup.complete.submitError")}
        />
      )}

      <Button
        type="primary"
        size="large"
        block
        style={{ marginTop: 24 }}
        onClick={handleCreate}
        loading={submitting}
        disabled={submitting}
      >
        {t("signup.details.createAccount")}
      </Button>
    </AuthCard>
  );
};
